import {
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import type {
  SetupMangoPayDirectDebitResidentRequest,
  SetupMangoPayDirectDebitResponse,
} from '@pflegenavi/frontend/api-nursing-home';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import type { PaymentInitiatedBy } from '@pflegenavi/shared/api';
import { DirectDebitStatus } from '@pflegenavi/shared/api';
import {
  HeaderBreadcrumbs,
  Modal,
  ModalInner,
} from '@pflegenavi/web-components';
import type { Country, MangopayBankAccountOwner } from '@pflegenavi/shared/api';
import type { UseMutationResult } from '@tanstack/react-query';
import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DirectDebitStatusCard } from '../components/directDebit/DirectDebitStatusCard';
import { CancelDirectDebitMangopay } from '../components/directDebit/mangoPay/CancelDirectDebitMangopay';
import { ManualPaymentCard } from '../components/directDebit/mangoPay/ManualPaymentCard';
import { SetupMangopayDirectDebit } from '../components/directDebit/mangoPay/SetupMangopayDirectDebit';
import { PaymentInitiationInfo } from '../components/directDebit/PaymentInitiationInfo';

// Define proper types for the hooks
export type CancelMandateHook = () => UseMutationResult<
  void,
  unknown,
  { paymentProcessorMode: 'live' | 'test'; resident_id: string; tenant: string }
>;

interface MangopayDirectDebitPageViewProps {
  // Data props
  countries: Country[];
  mandate?: {
    data?: {
      mandate?: {
        status?: 'active' | 'inactive' | 'pending';
        document_url?: string;
      };
      bank_account?: {
        iban?: string;
        owner_name?: string;
        owner_address?: {
          city?: string;
          address_line_1?: string;
          address_line_2?: string;
          region?: string;
          country?: string;
          postal_code?: string;
        };
      };
    };
  };
  activeBalance?: { amount?: number };

  // Loading states
  isLoading: boolean;
  isResidentPending: boolean;

  // Computed values
  mandateActive: boolean;
  mandatePending: boolean;
  mandateDocumentUrl?: string;
  owner: MangopayBankAccountOwner;
  isFamilyMemberOwner: boolean;
  hasFailedPayment: boolean;
  hasPendingFamilyPayment?: boolean;
  paymentInitiation?: PaymentInitiatedBy;

  // Disable flags based on resident state
  isPageDisabled?: boolean;
  isSepaFormDisabled?: boolean;
  isPaymentDisabled?: boolean;

  // Callbacks
  setupMandate: (
    body: SetupMangoPayDirectDebitResidentRequest['body']
  ) => Promise<SetupMangoPayDirectDebitResponse>;
  confirmOwnerChange: () => Promise<void>;
  cancelOwnerChange: () => void;
  onCancelMandate: () => void;

  // Modal states
  showSwitchBankAccountModal: boolean;

  // Component overrides with defaults
  SetupMangopayDirectDebitComponent?: React.ComponentType<{
    countries: Country[];
    owner: MangopayBankAccountOwner;
    mandateActive: boolean;
    mandatePending: boolean;
    mandateDocumentUrl?: string;
    onCancelMandate: () => void;
    bankAccount: {
      iban: string;
      owner_name: string;
      owner_address: {
        city: string;
        address_line_1: string;
        address_line_2: string;
        region: string;
        country: string;
        postal_code: string;
      };
    };
    setupMandate: (
      body: SetupMangoPayDirectDebitResidentRequest['body']
    ) => Promise<SetupMangoPayDirectDebitResponse>;
    PaymentInitiationInfoComponent?: React.ComponentType;
    isDisabled?: boolean;
  }>;
  DirectDebitStatusCardComponent?: React.ComponentType<{
    status: DirectDebitStatus;
    paymentInitiation?: PaymentInitiatedBy;
  }>;
  CancelDirectDebitMangopayComponent?: React.ComponentType<{
    status: DirectDebitStatus;
    useDeleteDirectDebitMandate: CancelMandateHook;
    isFamilyMember: boolean;
    isDisabled?: boolean;
  }>;
  ManualPaymentCardComponent?: React.ComponentType<{
    disabled: boolean;
    activeBalance?: number;
    isActiveBalanceLoading: boolean;
    mandateActive: boolean;
    hasFailedPayment: boolean;
  }>;
  PaymentInitiationInfoComponent?: React.ComponentType;

  // Hook for CancelDirectDebitMangopay
  useDeleteDirectDebitMandate: CancelMandateHook;
}

export const MangopayDirectDebitPageView: FC<
  MangopayDirectDebitPageViewProps
> = ({
  countries,
  mandate,
  activeBalance,
  isLoading,
  isResidentPending,
  mandateActive,
  mandatePending,
  mandateDocumentUrl,
  owner,
  isFamilyMemberOwner,
  hasFailedPayment,
  hasPendingFamilyPayment = false,
  paymentInitiation,
  setupMandate,
  confirmOwnerChange,
  cancelOwnerChange,
  onCancelMandate,
  showSwitchBankAccountModal,
  isPageDisabled = false,
  isSepaFormDisabled = false,
  isPaymentDisabled = false,
  SetupMangopayDirectDebitComponent = SetupMangopayDirectDebit,
  DirectDebitStatusCardComponent = DirectDebitStatusCard,
  CancelDirectDebitMangopayComponent = CancelDirectDebitMangopay,
  ManualPaymentCardComponent = ManualPaymentCard,
  PaymentInitiationInfoComponent = PaymentInitiationInfo,
  useDeleteDirectDebitMandate,
}) => {
  const { t } = useTranslation();

  return (
    <Container>
      <HeaderBreadcrumbs
        heading={t('fm-web-navigation.direct-debit')}
        links={[
          {
            name: t('fm-web-navigation.direct-debit'),
            href: fmAppMainPages.DIRECT_DEBIT,
          },
          { name: t('common.overview') },
        ]}
        sx={{
          pb: 0,
        }}
      />

      <Grid container direction="row" alignItems="flex-start" spacing={3}>
        <Grid item xs={12} md={7} lg={8}>
          {!isLoading ? (
            <SetupMangopayDirectDebitComponent
              countries={countries}
              owner={owner}
              mandateActive={mandateActive}
              mandatePending={mandatePending}
              mandateDocumentUrl={mandateDocumentUrl}
              onCancelMandate={onCancelMandate}
              bankAccount={{
                iban: mandate?.data?.bank_account?.iban ?? '',
                owner_name: mandate?.data?.bank_account?.owner_name ?? '',
                owner_address: {
                  city: mandate?.data?.bank_account?.owner_address?.city ?? '',
                  address_line_1:
                    mandate?.data?.bank_account?.owner_address
                      ?.address_line_1 ?? '',
                  address_line_2:
                    mandate?.data?.bank_account?.owner_address
                      ?.address_line_2 ?? '',
                  region:
                    mandate?.data?.bank_account?.owner_address?.region ?? '',
                  country:
                    mandate?.data?.bank_account?.owner_address?.country ?? '',
                  postal_code:
                    mandate?.data?.bank_account?.owner_address?.postal_code ??
                    '',
                },
              }}
              setupMandate={setupMandate}
              PaymentInitiationInfoComponent={PaymentInitiationInfoComponent}
              isDisabled={
                isPageDisabled || isSepaFormDisabled || isPaymentDisabled
              }
            />
          ) : (
            <LoadingContainer alignItems="center">
              <CircularProgress variant="indeterminate" />
            </LoadingContainer>
          )}
        </Grid>
        <Grid item xs={12} md={5} lg={4}>
          <Stack gap={2}>
            <DirectDebitStatusCardComponent
              status={getDirectDebitStatus(
                owner ? mandate?.data?.mandate?.status : 'inactive'
              )}
              paymentInitiation={paymentInitiation}
            />
            <CancelDirectDebitMangopayComponent
              status={getDirectDebitStatus(
                owner ? mandate?.data?.mandate?.status : 'inactive'
              )}
              useDeleteDirectDebitMandate={useDeleteDirectDebitMandate}
              isFamilyMember={isFamilyMemberOwner}
              isDisabled={hasPendingFamilyPayment}
            />
            {!isPaymentDisabled && (
              <ManualPaymentCardComponent
                disabled={!owner || isPageDisabled}
                activeBalance={activeBalance?.amount}
                isActiveBalanceLoading={isResidentPending}
                mandateActive={mandateActive}
                hasFailedPayment={hasFailedPayment}
              />
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Modal for confirming bank account switch */}
      <Modal open={showSwitchBankAccountModal} handleClose={cancelOwnerChange}>
        <ModalInner>
          <SwitchBankAccountModalContainer>
            <Typography color="primary" fontWeight={700} sx={{ mb: 2 }}>
              {t('mangopay.direct-debit.switch-bank-account.title')}
            </Typography>
            <Typography color="grey.800" sx={{ mb: 3 }}>
              {t('mangopay.direct-debit.switch-bank-account.description')}
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={cancelOwnerChange}>
                {t('actions.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={confirmOwnerChange}
              >
                {t('actions.save')}
              </Button>
            </Stack>
          </SwitchBankAccountModalContainer>
        </ModalInner>
      </Modal>
    </Container>
  );
};

const LoadingContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    mt: 2,
  })
);

const SwitchBankAccountModalContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    width: 450,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
  })
);

function getDirectDebitStatus(
  status?: 'active' | 'inactive' | 'pending'
): DirectDebitStatus {
  if (!status) {
    return DirectDebitStatus.Inactive;
  }
  switch (status) {
    case 'active':
      return DirectDebitStatus.Active;
    case 'pending':
      return DirectDebitStatus.Pending;
    default:
      return DirectDebitStatus.Inactive;
  }
}
